import React from 'react';
import {AsyncSelect} from '@atlaskit/select';
import {confluenceService} from "../../api/ConfluenceService";
import {useFlags} from "@atlaskit/flag";
import {errorFlag, successFlag} from "../../model/flags";
import Button from "@atlaskit/button/new";
import {FormFooter, Label} from "@atlaskit/form";
import {menuSettingsService} from "../../api/MenuSettingsService";
import {useAsync} from "react-use";

interface GroupOption {
    value: string;
    label: string;
}

export const AsyncGroupSelect = () => {
    const [selectedGroups, setSelectedGroups] = React.useState<Array<GroupOption>>([]);
    const [initLoading, setInitLoading] = React.useState(true);
    const {showFlag} = useFlags();

    useAsync(async () => {
        const initGroups: GroupOption[] = [];
        setInitLoading(true);

        try {
            const editorGroups = await menuSettingsService.getEditorGroups();
            for (const groupId of editorGroups.groupIds) {
                const group = await confluenceService.getGroupById(groupId);
                initGroups.push({value: group.id, label: group.name});
            }
        } catch (e) {
            handleOperationError("There was an error while fetching editor groups. Please try again later or contact support.");
        }

        setInitLoading(false);
        setSelectedGroups(initGroups);
    });

    const handleOperationError = (error?: string) => {
        const errMsg = error ?? "There was an error. Please try again later or contact support.";
        showFlag(errorFlag({title: "Error!", description: errMsg}));
    }

    async function saveGroups() {
        try {
            await menuSettingsService.setEditorGroups({groupIds: selectedGroups.map(group => group.value)});
            showFlag(successFlag({title: "Success!", description: "The groups have been saved."}));
        } catch (e) {
            handleOperationError("There was an error while saving editor groups. Please try again later or contact support.");
        }
    }

    const changeGroups = (groups: any) => {
        setSelectedGroups(groups);
    }

    const searchGroups = async (inputValue: string) => {
        try {
            return await confluenceService.getGroupsByQuery(inputValue).then(groups => groups.map(group => ({value: group.id, label: group.name})));
        } catch (e) {
            handleOperationError("There was an error while searching for groups. Please try again later or contact support.");
        }
    }

    const promiseOptions = (inputValue: string) => searchGroups(inputValue);

    return (
        <div className={"group-select"}>
            <Label htmlFor="group-select-input">Type to search for groups</Label>
            <AsyncSelect
                inputId="group-select-input"
                isMulti
                value={selectedGroups}
                placeholder="Select group(s)"
                defaultOptions
                isLoading={initLoading}
                loadOptions={promiseOptions}
                onChange={(groups) => changeGroups(groups)}
            />
            <FormFooter align={"start"}>
                <Button appearance={"primary"} onClick={saveGroups}>Save</Button>
            </FormFooter>
        </div>);
};
